import type { ReactNode } from 'react';
import {
  BreakdownPanel,
  ProvenanceFootnote,
  breakdownRows,
  type BreakdownRow,
} from '../Breakdown/Breakdown.js';
import { provenanceFor } from '../Breakdown/provenance.js';
import { DataTable, type Column } from '../DataTable/DataTable.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { formatNumberNarrow, formatValue, formatValueExact } from '../MetricCell/MetricCell.js';
import { color, font, fontSize, radius } from '../../theme/tokens.js';
import {
  NO_DECOMPOSITION,
  SERIES_FULL,
  SERIES_LABEL,
  formatBucketWindow,
  type ChartData,
  type ChartPoint,
  type ChartRowKey,
} from './chartData.js';

/**
 * The chart's figures, as a table (owner request 2026-09-09).
 *
 * The plot answers *what shape did this take*; there was no surface answering *what were the
 * numbers* except one bucket at a time under the pointer. This is the same response, the same
 * buckets and the same series, reached from the two-glyph toggle in the card's own header.
 *
 * ## The series are the ROWS and the buckets are the COLUMNS (owner request 2026-09-09)
 *
 * It shipped the other way round — a row per bucket, a column per series — and was turned the same
 * day. The layout now **is** the chart's own axes: metrics down the side, time left to right,
 * oldest to newest, exactly as the plot draws them. Three things follow from the orientation, and
 * each is why the first cut was wrong rather than merely different.
 *
 * - **Time reads along a row**, so a series' trajectory is one horizontal scan — the reading the
 *   plot gives for free and the transposed table did not.
 * - **Sorting is gone**, and it should be: the columns are an ordered axis. Ranking buckets by TOA
 *   would shuffle the x axis, and ranking *rows* means ranking TOA against JOBS, which is four
 *   different units in one comparison.
 * - **The total is a trailing column, not a row.** With buckets down the side, a totals row summed
 *   each bucket's own figure; across, each series totals its own axis and nothing else.
 *
 * ## What it must not become
 *
 * **It is not a second query.** Nothing here fetches, derives or aggregates beyond summing the row
 * it is standing in: every figure is a `points[]` value the plot draws from the same array, so the
 * two views cannot disagree. A figure that appears in this table and nowhere in the chart response
 * is the defect to look for first.
 *
 * **It carries no trend at all** (owner request 2026-09-10). It shipped with the summary strip's
 * bucket-over-bucket change under every figure and now shows the figure alone: the columns *are*
 * the comparison, so a reader scanning a row already sees the movement, and the glyph was the
 * same one Region Performance uses for FR-054's fixed 30-day window over a different window
 * entirely. ⚠ Restoring it puts two meanings on one arrow, which is what `<MetricCell>` is
 * deliberately not routed through here to avoid.
 *
 * ## Why `<DataTable>` rather than a grid of its own
 *
 * The card chrome, the grid, the paddings, the scrollport in both axes, and — the part that is not
 * optional — a `<Disclosure>` on every column header carrying its full label, which Constitution
 * gate 2 exempts no column from. A bespoke grid would have re-implemented all five and drifted
 * from the twelve other tables in the product. The matrix needs two things those tables do not,
 * and both arrived as opt-in props rather than as local markup: `columnRules` (a hairline at every
 * column boundary, because a cell here is read as the intersection of a row and a column) and
 * `stickyFirstColumn` (the metric names survive a sideways scroll). `featured` stays off — the
 * ring belongs to the chart card this sits inside, and drawing it twice marks nothing.
 */

/**
 * The rows to draw, in order: the series the plot is drawing (what the legend has left visible),
 * then the response's table-only rows (FR-D26).
 *
 * `color` is `null` on a table-only row and the label column draws **no dot** for it. The dot is
 * not decoration — it is what ties a row to the line it was a moment ago and to the chip that
 * hides it — so a hue on a row with no line would promise a curve that is not on the plot.
 *
 * `sums` is what the `TOTAL` cell obeys. A stock has never summed; `uniqueSubs` does not either,
 * for a different reason, and the wire carries the answer rather than the renderer inferring it —
 * see `seriesSums` in `backend/src/domain/series.ts`.
 */
export interface ChartTableSeries {
  key: ChartRowKey;
  kind: 'stock' | 'period';
  color: string | null;
  sums?: boolean;
}

export interface ChartTableProps {
  data: ChartData | null;
  /** The chart's accessible name, so the table announces as the same subject the plot did. */
  title: string;
  series: ChartTableSeries[];
  loading?: boolean;
  /**
   * The height the card's body is reserved at — `CHART_BODY_HEIGHT`, the plot block's own sum.
   *
   * The matrix **grows its rows into it** rather than sitting short inside it, which is what stops
   * the card resizing when the view is switched (owner report 2026-09-09: it shrank going one way
   * and grew back the other). Omitted, rows fall back to their minimum and the table is as tall as
   * its contents — the right behaviour for any future caller that is not inside a chart card.
   */
  bodyHeight?: number;
  /**
   * `Envelope.syncedAt`, passed straight through to `formatBucketWindow` — see `<LineChart>`.
   *
   * The matrix needs it for the same reason the bubble does: its columns are buckets, and the
   * newest one's window ends at the sync rather than at the as-of day's calendar edge.
   */
  syncedAt?: string | null;
  /**
   * Renders the export control for one row — the matrix's action column (FR-093, owner request
   * 2026-09-14).
   *
   * ⚠ **Return `null` for a row that has no export, and most rows do.** Only `jobs` and `subs`
   * name a dataset the server can write: `toa` is an as-of stock with no row-level records behind
   * it, `offers` and the four table-only Delivery rows have no export collection at all. A
   * control on those would either be disabled everywhere — inviting a reader to hunt for a reason
   * — or would write a file that is not what the row counts.
   *
   * ⚠ **Opt-IN, exactly like `<DataTable>`'s `columnRules`.** Omitted, the column does not exist
   * and the table is 68px narrower, which is what Delivery's matrix wants: its rows are a
   * manager's or a recruiter's submissions and the export for those lives on the two drill-down
   * tables, keyed by person rather than by series.
   *
   * ⚠ **A node, not a key.** Each control owns a streaming progress reading, a consent round trip
   * and a completion dialog; that state has to survive the legend isolating a series and
   * re-rendering the row set, which it cannot do from inside the table.
   */
  onExportRow?: (key: ChartRowKey) => ReactNode;
}

/**
 * Cell geometry: one height for every cell in a render, computed once from the reservation.
 *
 * Three things depend on it being uniform. `columnRules` stretches every cell to its row's height,
 * so cells that disagreed about how tall they are would draw rules of two different lengths — the
 * reason every cell takes its height from here rather than from its own contents. The loading
 * state has to reserve the height the response will occupy, which a known row count and one row
 * height make arithmetic rather than a guess. And the rows **fill the card body** so switching
 * views does not resize the card (`bodyHeight`, and `rowHeightFor` below).
 *
 * ⚠ **The floor is 36 because Delivery's matrix is SEVEN rows** (2026-09-11, FR-D26): three
 * lines plus four table-only ones. The floor is what decides whether they fit the reservation,
 * and the ladder is worth stating because each step of it was a card that grew on the way into
 * the table and shrank back on the way out — the exact flinch `bodyHeight` exists to prevent,
 * arriving from the row count instead of the view:
 *
 * ```text
 * rows   height   total    verdict
 *  4      65      292      the region charts — never reach the floor
 *  6      43      292      fits
 *  7      37      294      fits, and it is the last row count that does
 *  8      36      324      does NOT fit — 295px of reservation is 29px short
 * ```
 *
 * So an **eighth** row is the tripwire: do not answer it by dropping this number again (32px is
 * a cramped cell), answer it by giving the matrix a scrolling body of its own. Raising the floor
 * back toward 46 puts the growth back on the one page that has the most rows.
 */
export const MIN_ROW_HEIGHT = 36;
/**
 * The ceiling on that growth. Without it, isolating one series with a legend chip would stretch a
 * single row over the whole reserved body — a 266px tall cell holding one line of mono, which
 * reads as a rendering fault rather than as a table.
 *
 * At 72 the four series fill the reservation almost exactly (four 65px rows), Delivery's three and
 * any legend-narrowed set stop here, and the leftover is centred by the caller.
 */
const MAX_ROW_HEIGHT = 72;
/**
 * `<DataTable>`'s header row: 8px of padding either side of a 9px label.
 *
 * An estimate, and it is allowed to be: it is subtracted to decide how tall the rows may grow, so
 * a pixel or two of error lands in the slack the caller centres. Nothing is positioned from it.
 */
const HEADER_HEIGHT = 29;

/**
 * How tall each row is: fill the reserved body, floor at `MIN_ROW_HEIGHT`, cap at
 * `MAX_ROW_HEIGHT`.
 *
 * The `rows - 1` is the hairlines between them — they are real pixels, and leaving them out makes
 * the table overshoot the reservation by a row's worth of borders on a wide series set.
 */
function rowHeightFor(bodyHeight: number | undefined, rows: number): number {
  if (!bodyHeight || rows <= 0) return MIN_ROW_HEIGHT;
  const available = bodyHeight - HEADER_HEIGHT - (rows - 1);
  return Math.max(MIN_ROW_HEIGHT, Math.min(MAX_ROW_HEIGHT, Math.floor(available / rows)));
}
/**
 * The gutter **inside** a cell, on top of the 6px `<DataTable>` puts on every cell wrapper.
 *
 * 6, not the 4px `<MetricCell>` uses, because a figure here has a rule a few pixels to its right
 * where a Region Performance figure has open space. It stops there rather than going wider: the
 * header row's own gutter is 10px and is `<DataTable>`'s to set, so 12px in the body keeps the
 * date and the column of figures under it aligned to within 2px — go to 16 and the header text
 * visibly overhangs its own column.
 *
 * ⚠ So the room this table needed came from the **tracks**, not from here — see below.
 */
const CELL_PAD_X = 6;

/**
 * Column widths, sized from the **formatter** plus that gutter rather than from today's data.
 *
 * `formatNumberNarrow` compacts at 1,000 to exactly **one** decimal here (owner request
 * 2026-09-10, §6.1.1's one exception), so no figure exceeds six glyphs (`999.9K`). At IBM Plex
 * Mono's fixed 600/1000-em advance that is 41.4px, and a bucket track carries it in the 50px its
 * two gutters leave — `<DataTable>`'s 6px on each cell wrapper plus `CELL_PAD_X` — with ~9px still
 * clear at the worst case the formatter can produce and ~22px on the figures these charts actually
 * draw (`9.4K`, `505`). No snapshot can outgrow it, which is the property a width measured against
 * one snapshot does not have (`TWO_FIGURE_TRACK` was overtaken twice).
 *
 * ⚠ **The whole table FITS, and that is what these numbers are for** (owner request 2026-09-10).
 * The ceiling is a 1,280 viewport's ~1,082px of card interior with the rail collapsed — so the
 * twelve buckets and the total are on screen at once and the wrapper's `overflow` never engages.
 * They are a **budget**, not a fit to today's data: widening any one of them by 6px puts the
 * scrollbar back at that width, which is the regression to watch for.
 *
 * ⚠ **The tracks were narrowed 74→68 and 88→84 to buy the action column** (owner request
 * 2026-09-14, FR-093). It was `100 + 12×74 + 88 = 1,076`; it is now `100 + 12×68 + 84 + 68 =
 * 1,068`, so the export column is paid for out of the figures rather than out of the no-scroll
 * decision — which was the choice put to the owner, the alternative being a 1,144px table that
 * scrolls at every laptop width.
 *
 * ⚠ **68 is a floor derived from the FORMATTER.** `formatNumberNarrow`'s worst case `999.9K` is
 * 41.4px of IBM Plex Mono at its fixed 600/1000-em advance, and a bucket cell's real gutter is
 * `CELL_PAD_X` on each side — **12px, not the 24px the note above claims**. The missing half is
 * `<DataTable>`'s own `padding: 0 6px`, which it declares and then **clears on every interior
 * cell**: it overrides `paddingLeft`/`paddingRight` per edge and hands `undefined` to the columns
 * that are not on one, and React removes a property it is given `undefined` rather than leaving
 * the shorthand's value. So a 68px track carries 41.4px of ink in a 56px box with ~14.6px clear,
 * where 74px gave ~20.6px, and the true hard floor is ~54px.
 *
 * ⚠ **That extra room is NOT available to spend.** 68px is what pairs with the 68px action column
 * to land at 1,068px, and the slack is the margin this budget has always had without knowing it.
 * Narrowing further to buy a second column is still not on: the next thing to give would be the
 * formatter, and a column that compacts differently from the panel beside it is the drift
 * `COMPACT_FROM` exists to prevent.
 *
 * ⚠ **Do not "fix" the cleared gutter.** Restoring `<DataTable>`'s 6px would add 12px of gutter to
 * every cell of every table in the product and move four width budgets at once — this one,
 * `TWO_FIGURE_TRACK`, Region Performance's 1,270px and `breakdownMinWidth`. It is recorded here
 * because the arithmetic above depends on it, not as a defect to tidy.
 *
 * ⚠ **It reached 96px by a route worth knowing, and the route is closed.** The first cut was 74px
 * and read as congested (owner report 2026-09-09) — but a cell then held a seven-glyph two-decimal
 * figure (48.3px in that same 50px box, ~2px clear) with the bucket-over-bucket change on a second
 * line under it. Both are gone: no change line (2026-09-10) and one decimal (2026-09-10). So this
 * is the same track width around **half** the ink, and restoring either of those two without
 * widening these back is what would re-create the complaint.
 *
 * ⚠ **`stickyFirstColumn` stays on even though nothing scrolls at these widths.** It is the floor's
 * safety net, not its consequence: below ~1,076px of interior — a narrow window, a pinned sidebar
 * drawer on a small laptop, a zoomed page — the wrapper scrolls again, and a horizontal scroll that
 * takes the row labels off screen leaves a reader looking at four rows of unattributed figures.
 *
 * The tracks are `1fr` above their floor, so on a wider card they share the room out rather than
 * leaving it at the right edge. The label column is fixed, not elastic: it is the pinned one, and a
 * frozen column that changed width with its content would move the seam as the data changed.
 */
const LABEL_WIDTH = 100;
const BUCKET_WIDTH = 68;
const TOTAL_WIDTH = 84;
const LABEL_COLUMN = `${LABEL_WIDTH}px`;
const BUCKET_COLUMN = `minmax(${BUCKET_WIDTH}px, 1fr)`;
const TOTAL_COLUMN = `minmax(${TOTAL_WIDTH}px, 1fr)`;

/**
 * The export column's track (FR-093, owner request 2026-09-14).
 *
 * ⚠ **This is the column FR-088 refused, and the refusal was right at the time.** That note reads:
 * "the button is in the card HEADER, not an actions column in the matrix (which is where it was
 * asked for) — that table's floor is 1,076px against ~1,082px of card interior, so even a 40px
 * column restores the horizontal scroll the 2026-09-10 owner decision removed". Both halves of
 * that still hold; what changed is that the owner chose which of the two to pay with
 * (2026-09-14), and the answer was the bucket tracks rather than the scrollbar.
 *
 * 68px for the reason the other four tables' action columns are 68px: the column carries the word
 * `Action`, ~33px of ink at `fontSize.tableHeader`, and the header cell spends 10px of padding on
 * the left and 20px on the right as the last column.
 */
const ACTION_COLUMN_WIDTH = 68;
const ACTION_COLUMN = `${ACTION_COLUMN_WIDTH}px`;

/**
 * Twelve buckets and a total, plus the pinned label column and the export column when there is
 * one — which is what the whole table has to stay under to fit a laptop card without a horizontal
 * scroll (see above).
 *
 * ⚠ **The action column is in the sum, and the sum still has to clear 1,076px.** With it:
 * `100 + 12×68 + 84 + 68 = 1,068`. Without it: `100 + 12×68 + 84 = 1,000`. A caller that supplies
 * no control gains no track, exactly as the four row-subject tables do — so Delivery's matrix,
 * which has no per-row export, simply gets a narrower table rather than an empty column.
 */
const MIN_TABLE_WIDTH = (bucketCount: number, hasActions: boolean) =>
  `${LABEL_WIDTH + BUCKET_WIDTH * bucketCount + TOTAL_WIDTH + (hasActions ? ACTION_COLUMN_WIDTH : 0)}px`;

/** What the reservation draws before the first response lands. */
const PLACEHOLDER_BUCKETS = 12;
const PLACEHOLDER_SERIES = 4;

export function ChartTable({
  data,
  title,
  series,
  loading,
  bodyHeight,
  syncedAt,
  onExportRow,
}: ChartTableProps) {
  const points = data?.points ?? [];
  const rowHeight = rowHeightFor(bodyHeight, series.length);

  /*
   * The skeleton is its own render rather than a table of placeholder rows.
   *
   * A `<DataTable>` column requires a label and a full label — here, the bucket's date and its
   * meaning — and neither exists before the response does. Inventing them would put twelve
   * fabricated dates in a header row; drawing the grid at the geometry the response will land in
   * reserves the same height and claims nothing.
   */
  if (points.length === 0 && loading) {
    const rows = Math.max(series.length, PLACEHOLDER_SERIES);
    return <MatrixSkeleton seriesCount={rows} rowHeight={rowHeightFor(bodyHeight, rows)} />;
  }

  /**
   * Breakdown rows for one series at one bucket — the hover bubble's decomposition, reached here
   * by keyboard as well as pointer.
   *
   * Deliberately the **same** `breakdownRows` the plot's bubble calls, including `includeSource:
   * key !== 'jobs'`: JOBS carries Target, Nursing and Allied and no Internal/External (owner
   * decision 2026-08-30, PRD §9.7.5.2), and a second list of axes here is how the two surfaces
   * would come to decompose one figure two ways.
   */
  const rowsFor = (key: ChartRowKey, index: number, total: number | null): BreakdownRow[] =>
    // ⚠ `NO_DECOMPOSITION` is the one exception, and it is FR-084's: the axes of a *distinct
    // candidate* count overshoot their own total, and parts that exceed the whole read as an
    // arithmetic fault. The cell keeps its figure and its provenance; it loses only the split it
    // cannot honestly give.
    data?.breakdown && !NO_DECOMPOSITION.has(key)
      ? breakdownRows({
          total,
          internal: data.breakdown.internal[index]?.[key] ?? null,
          target: data.breakdown.target[index]?.[key] ?? null,
          nursing: data.breakdown.nursing[index]?.[key] ?? null,
          allied: data.breakdown.allied[index]?.[key] ?? null,
          includeSource: key !== 'jobs',
        })
      : [];

  /**
   * The pinned row-header column: the series code, its hue, and its full name behind a hover.
   *
   * The dot is the legend chip's dot at the same size, and it is the one place the series colour
   * belongs in this table — it ties a row to the line it was a moment ago and to the chip that
   * hides it. Colouring the *figures* would have made four rows of tinted mono, which is the one
   * way to make a table harder to read than no table.
   */
  const labelColumn: Column<SeriesRow> = {
    key: 'metric',
    label: 'METRIC',
    fullLabel: 'The series each row reports — the same lines the chart draws',
    align: 'left',
    width: LABEL_COLUMN,
    render: (row) => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          height: rowHeight,
          padding: `0 ${CELL_PAD_X}px`,
        }}
      >
        {/*
          A dot only where there is a line to point at. A table-only row (FR-D26) draws the 8px
          anyway — as empty space, so the labels stay on one left edge: a ragged first column
          reads as two tables stacked, which is exactly what these rows are not.
        */}
        <span
          aria-hidden="true"
          style={{
            width: 8,
            height: 8,
            borderRadius: radius.circle,
            background: row.color ?? 'transparent',
            flexShrink: 0,
          }}
        />
        <Disclosure
          accessibleName={
            row.kind === 'stock'
              ? `${SERIES_FULL[row.key]} — an as-of count at each bucket end`
              : SERIES_FULL[row.key]
          }
          /*
           * The row header is where the series is *defined*, so it carries the lineage in full
           * — one panel per row rather than the same sentence repeated down thirteen cells.
           * The cells carry it too, because a reader hovering a figure should not have to know
           * to travel left to find out what it is; the header is simply the one that answers
           * when there is no bucket in the question.
           */
          panel={
            <span style={{ display: 'block' }}>
              <strong>{SERIES_FULL[row.key]}</strong>
              <ProvenanceFootnote provenance={provenanceFor(row.key)} />
            </span>
          }
        >
          <span
            className="mono"
            style={{
              fontFamily: font.mono,
              fontSize: fontSize.captionStrong,
              fontWeight: 700,
              color: color.ink,
              letterSpacing: '.2px',
            }}
          >
            {SERIES_LABEL[row.key]}
          </span>
        </Disclosure>
      </div>
    ),
  };

  /**
   * The in-progress bucket has to be marked HERE too (FR-085), not only in the plot.
   *
   * The two views are two readings of one response, so a bucket the line draws dashed and the
   * matrix draws like any other is the pair disagreeing about the one thing they must agree on.
   * A trailing `*` is all a 96px header has room for — `(partial)` does not fit — so the meaning
   * lives in `fullLabel`, which is what the header's disclosure reveals, and in the total's panel
   * below, which is the cell the partial bucket actually distorts.
   */
  const bucketColumns: Array<Column<SeriesRow>> = points.map((point, index) => {
    /*
     * The window the column covers (FR-087), in the header's disclosure rather than in every
     * cell: the column *is* the bucket, so stating it once per column says it for all four
     * series, where a copy per cell would repeat one fact forty-eight times in one table.
     *
     * ⚠ The same string the plot's hover bubble prints, from `chartData.ts` — the two views are
     * two readings of one response, and a matrix column that named a different window from the
     * bubble over the same bucket is the pair disagreeing about what the figure covers.
     */
    // Not named `window` — shadowing the global inside a browser component is a trap for the
    // next reader, not just for the linter.
    const covered = formatBucketWindow(point, syncedAt);
    const covers = covered ? ` (${covered})` : '';

    return {
      key: point.key,
      label: point.isPartial ? `${point.label}*` : point.label,
      fullLabel: point.isPartial
        ? `${point.label}${covers} — the period still in progress, counted only as far as the as-of date; each series' figure so far`
        : `${point.label}${covers} — each series' figure for this bucket`,
      align: 'right',
      width: BUCKET_COLUMN,
      // No `sortValue` anywhere: the columns are an ordered axis, and ranking them would shuffle it.
      render: (row) => {
        const value = row.point(index);
        return (
          <FigureCell
            rowHeight={rowHeight}
            seriesKey={row.key}
            bucketLabel={point.label}
            value={value}
            breakdown={rowsFor(row.key, index, value)}
          />
        );
      },
    };
  });

  /**
   * The export column (FR-093).
   *
   * ⚠ **Built from whether any row can export, not from whether the prop is set.** A caller that
   * supplies the render prop for a chart whose rows all decline it would otherwise get an empty
   * 68px track and the narrowed bucket widths that paid for it — the worst of both. So the
   * controls are rendered once, here, and the column exists only if at least one is real.
   *
   * ⚠ **No `stopPropagation` wrapper, deliberately unlike the other four action columns.** Those
   * sit in tables whose rows are buttons — `<DataTable>` puts an `onClick` and an Enter/Space
   * `onKeyDown` on the row `div`, so an export there fired *and* navigated. This table passes no
   * `onRowClick` at all: a matrix row is a series, and there is nowhere for it to go. Adding the
   * wrapper anyway would be harmless today and would quietly stop being harmless the moment a row
   * click meant something, which is exactly the kind of guard that gets copied without its
   * reason.
   */
  const rowControls = onExportRow
    ? new Map(series.map((line) => [line.key, onExportRow(line.key)] as const))
    : null;
  const hasActions =
    rowControls != null && [...rowControls.values()].some((node) => node != null && node !== false);

  const actionColumn: Column<SeriesRow> = {
    key: 'actions',
    label: 'Action',
    fullLabel: 'Action — export this row’s underlying records to a Google Sheet',
    align: 'center',
    width: ACTION_COLUMN,
    render: (row) => {
      const control = rowControls?.get(row.key);
      // ⚠ Nothing, never a disabled control: TOA and OFFERS have no export to offer, and a
      // permanently dead button in a four-row table reads as a fault rather than as an absence.
      if (!control) return null;
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            /*
             * ⚠ **`width: 100%` is what makes `justifyContent` mean anything here**, and its
             * absence is why the glyph sat at the left of the track while the header above it
             * looked centred. `columnRules` makes `<DataTable>` give every cell wrapper
             * `display: flex` with no `justify-content`, so the wrapper packs its children at
             * `flex-start` and this div is a flex ITEM shrunk to the 28px button — centring
             * inside a box exactly as wide as the thing being centred is a no-op.
             *
             * ⚠ It is the same reason the four row-subject action columns did NOT need it: none
             * of them sets `columnRules`, so their cell wrappers are plain blocks and a `flex`
             * child fills the width on its own. Copying their markup here is what produced the
             * bug.
             *
             * `width`, not `flex: 1`, to match `FigureCell` and `TotalCell` — every cell in this
             * table states its own full width and row height.
             */
            width: '100%',
            height: rowHeight,
          }}
        >
          {control}
        </div>
      );
    },
  };

  const totalColumn: Column<SeriesRow> = {
    key: 'total',
    label: 'TOTAL',
    fullLabel:
      'The series summed over every bucket drawn — a dash where the figure does not sum: an as-of count, or a count of distinct people',
    align: 'right',
    width: TOTAL_COLUMN,
    render: (row) => <TotalCell row={row} points={points} rowHeight={rowHeight} />,
  };

  return (
    <DataTable
      columns={[
        labelColumn,
        ...bucketColumns,
        totalColumn,
        ...(hasActions ? [actionColumn] : []),
      ]}
      rows={series.map((line) => toRow(line, points))}
      rowKey={(row) => row.key}
      /*
        `rows`, not `series`: since FR-D26 three of Delivery's six are figures the plot does not
        draw, and announcing them as series would tell a screen-reader user to go looking for
        lines that are not there.
      */
      caption={`${title} — ${series.length} rows over ${points.length} periods, oldest first${
        points[points.length - 1]?.isPartial ? ', the last still in progress' : ''
      }`}
      minWidth={MIN_TABLE_WIDTH(points.length, hasActions)}
      // A matrix is read across and down, so both boundaries are drawn — and the metric names have
      // to stay put while twelve buckets scroll past them.
      columnRules
      stickyFirstColumn
      // The total is a column here; a totals ROW would sum TOA, JOBS, SUBS and OFFERS together.
      showTotals={false}
      // Four rows never reach a cap, so a cap could only ever add a scrollbar nobody needs.
      maxBodyHeight={false}
      emptyMessage="No figures for this selection."
    />
  );
}

/**
 * One row: a series, its hue, and a reader into the response.
 *
 * `point(index)` rather than a materialised array of twelve values, so the row holds no copy of
 * the figures — the cell reads `points[index][key]` at render, which is the same read the plot
 * makes. A copy is how a table comes to show what the chart used to be drawing.
 */
interface SeriesRow {
  key: ChartRowKey;
  kind: 'stock' | 'period';
  /** `null` on a table-only row — it has no line for a dot to point at. */
  color: string | null;
  /** Whether the `TOTAL` cell may add the buckets up. */
  sums: boolean;
  point: (index: number) => number | null;
}

function toRow(line: ChartTableSeries, points: ChartPoint[]): SeriesRow {
  return {
    key: line.key,
    kind: line.kind,
    sums: line.sums ?? line.kind !== 'stock',
    /*
     * The themed hue, resolved the way the plot resolves it — `domain/series.ts` owns which colour
     * means which series and the dark palette overrides it for legibility (see `scaled` in
     * `<LineChart>`). No fallback is passed because the dot is decoration: an unstyled dot is a
     * cosmetic loss, where an unstyled *line* would be an invisible series.
     */
    color: line.color === null ? null : `var(--c-series-${line.key})`,
    point: (index) => points[index]?.[line.key] ?? null,
  };
}

/**
 * One figure and the axis decomposition behind it — and nothing else (owner request 2026-09-10).
 *
 * **No trend.** The cell carried the bucket-over-bucket change on a second line until 2026-09-10;
 * the figure now stands alone, because the reader already has the previous bucket in the cell to
 * the left and an arrow here reads as Region Performance's fixed 30-day one (FR-054) over a
 * window that is not that at all.
 *
 * Not `<MetricCell>`, still: that component's whole contract is the `Metric.d30` arrow, so
 * routing through it would put the trend back by construction. What is shared instead is
 * everything that should be — `formatValue`/`formatValueExact` for the face and the exact figure,
 * `<Disclosure>` for hover-and-focus parity, and `<BreakdownPanel>` for the panel — so a cell here
 * and a cell in Region Performance compact at the same threshold and announce a number the same
 * way.
 *
 * ⚠ **The face is the ONE thing that differs**: `formatNumberNarrow`, one decimal rather than two
 * (owner request 2026-09-10), because thirteen `1fr` columns make a glyph cost thirteen glyphs
 * across a row. `formatValueExact` is untouched by it — `EXACT_OF` pairs the two — so the panel
 * and the accessible name still carry every digit (FR-062), which is what makes a shorter face a
 * display choice rather than a loss.
 */
function FigureCell({
  rowHeight,
  seriesKey,
  bucketLabel,
  value,
  breakdown,
}: {
  rowHeight: number;
  seriesKey: ChartRowKey;
  bucketLabel: string;
  value: number | null;
  breakdown: BreakdownRow[];
}) {
  const exact = formatValueExact(value);

  // The series and the bucket, because a figure announced on its own names neither axis it sits
  // on — the two things the sighted reader gets from the row header and the column header.
  const accessibleName =
    value === null
      ? `${SERIES_FULL[seriesKey]} at ${bucketLabel}: no data`
      : `${SERIES_FULL[seriesKey]} at ${bucketLabel}: ${exact}`;

  return (
    <Disclosure
      accessibleName={accessibleName}
      panel={
        breakdown.length > 0 ? (
          <BreakdownPanel
            title={`${SERIES_LABEL[seriesKey]} · ${bucketLabel}`}
            total={exact}
            rows={breakdown}
            provenance={provenanceFor(seriesKey)}
          />
        ) : (
          <span>
            <strong>{`${SERIES_LABEL[seriesKey]} · ${bucketLabel}`}</strong>
            <span style={{ display: 'block' }}>{exact}</span>
            <ProvenanceFootnote provenance={provenanceFor(seriesKey)} />
          </span>
        )
      }
      style={{
        // The whole cell is the trigger, not just the glyphs, so a column this narrow has no dead
        // strip in it that answers nothing.
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
        height: rowHeight,
        padding: `0 ${CELL_PAD_X}px`,
        cursor: 'default',
      }}
    >
      <span
        className="mono"
        style={{
          fontFamily: font.mono,
          fontSize: fontSize.captionStrong,
          fontWeight: 500,
          color: value === null ? color.inkFaint : color.inkNumeric,
          whiteSpace: 'nowrap',
        }}
      >
        {formatValue(value, formatNumberNarrow)}
      </span>
    </Disclosure>
  );
}

/**
 * The trailing total, and the one cell this table has to refuse to compute.
 *
 * **A flow sums; a stock does not.** TOA is an as-of count evaluated at `bucket.end` alone, so
 * twelve of them added together is not a quantity — it is twelve overlapping snapshots of the same
 * travellers counted twelve times. So the cell renders `—`, and its panel says what the honest
 * answer is instead: the latest bucket's value. Same rule and same reason as `RATIO_KEYS` in
 * `api/regions.ts`, which keeps `sumMetrics` from folding Cov % into a plausible 420%.
 *
 * ⚠ **Since 2026-09-11 the refusal is the served `sums` flag, not `kind === 'stock'`** (FR-D26).
 * `UNIQ SUBS` is a *period* row that still does not add — distinct candidates are a set, in
 * buckets exactly as in scopes — and inferring "period ⇒ sums" would have printed a plausible
 * over-count under a column headed `TOTAL`. The two refusals keep their own sentences below.
 *
 * ⚠ Do not "fix" the dash by summing it, and do not put the latest figure on the face either: a
 * column headed `TOTAL` carrying a latest-value in one row and sums in the others is the kind of
 * heterogeneity nothing on screen would catch.
 *
 * A flow's sum skips nulls and is `null` only when every bucket is (FR-055) — a window where one
 * bucket failed to compute still has a total over the ones that did, and the panel names how many
 * of the buckets it covered so a partial window cannot masquerade as a whole one. Since FR-085 it
 * names the other kind of partial too: the newest bucket is the period in progress, so the sum is
 * eleven whole periods and part of a twelfth.
 */
function TotalCell({
  row,
  points,
  rowHeight,
}: {
  row: SeriesRow;
  points: ChartPoint[];
  rowHeight: number;
}) {
  const values = points.map((_point, index) => row.point(index));
  const present = values.filter((value): value is number => value !== null);

  if (!row.sums) {
    const latest = values[values.length - 1] ?? null;
    /*
     * Two rows refuse the total and they refuse it for two different reasons, so they say two
     * different things. TOA is a **stock**: twelve as-of counts added is the same book counted
     * twelve times. `UNIQ SUBS` is a period figure that still does not add: a candidate submitted
     * in two buckets is one person in each and one person overall, so twelve monthly counts
     * summed is an upper bound on the year's candidates, not the year's figure (FR-084 measured
     * the same arithmetic from the other end — 19,626 submissions over 12M are 5,646 people).
     *
     * ⚠ The sentence is chosen by `kind`, not by the key, so a future non-summing row gets one of
     * the two readings rather than silence.
     */
    const stock = row.kind === 'stock';
    const reason = stock
      ? 'An as-of count at each bucket end — adding them counts the same book twelve times.'
      : 'Distinct candidates per bucket — the same person can appear in several, so these do not add.';

    return (
      <TotalFace
        rowHeight={rowHeight}
        display="—"
        accessibleName={`${SERIES_FULL[row.key]}: no total — ${
          stock ? 'an as-of count does not sum' : 'distinct counts do not sum'
        }. Latest ${formatValueExact(latest)}`}
        panel={
          <span>
            <strong>{`${SERIES_LABEL[row.key]} · no total`}</strong>
            <span style={{ display: 'block', marginTop: 3 }}>{reason}</span>
            <span style={{ display: 'block', marginTop: 3, fontFamily: font.mono }}>
              {`Latest ${formatValueExact(latest)}`}
            </span>
            <ProvenanceFootnote provenance={provenanceFor(row.key)} />
          </span>
        }
        muted
      />
    );
  }

  const total = present.length === 0 ? null : present.reduce((sum, value) => sum + value, 0);
  // The newest bucket is the period in progress (FR-085), so this total is eleven whole periods
  // plus part of a twelfth. Said on the face it would crowd a 112px cell; unsaid anywhere it would
  // make the column look like a comparable window. The panel is where it belongs.
  const partial = points[points.length - 1]?.isPartial === true;

  return (
    <TotalFace
      rowHeight={rowHeight}
      display={formatValue(total, formatNumberNarrow)}
      accessibleName={`${SERIES_FULL[row.key]} total over ${present.length} periods${
        partial ? ', the last still in progress' : ''
      }: ${formatValueExact(total)}`}
      panel={
        <span>
          <strong>{`${SERIES_LABEL[row.key]} · total`}</strong>
          <span style={{ display: 'block', marginTop: 3, fontFamily: font.mono }}>
            {formatValueExact(total)}
          </span>
          <span style={{ display: 'block', marginTop: 3 }}>
            {`Summed over ${present.length} of ${values.length} periods drawn`}
            {partial ? ', the last still in progress' : ''}
          </span>
          <ProvenanceFootnote provenance={provenanceFor(row.key)} />
        </span>
      }
    />
  );
}

function TotalFace({
  rowHeight,
  display,
  accessibleName,
  panel,
  muted = false,
}: {
  rowHeight: number;
  display: string;
  accessibleName: string;
  panel: ReactNode;
  muted?: boolean;
}) {
  return (
    <Disclosure
      accessibleName={accessibleName}
      panel={panel}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
        height: rowHeight,
        padding: `0 ${CELL_PAD_X}px`,
        cursor: 'default',
      }}
    >
      <span
        className="mono"
        style={{
          fontFamily: font.mono,
          fontSize: fontSize.captionStrong,
          fontWeight: 700,
          color: muted ? color.inkFaint : color.ink,
          whiteSpace: 'nowrap',
        }}
      >
        {display}
      </span>
    </Disclosure>
  );
}

/**
 * The reservation — the matrix's geometry with a bar in every cell.
 *
 * Laid out from the **same** `rowHeightFor` the loaded table uses, so the card holds its exact
 * height across a fetch: a skeleton that is the wrong size is worse than none, which is the rule
 * `CHART_HEIGHT` and the plot's two reserved rows already follow one view over.
 */
function MatrixSkeleton({
  seriesCount,
  rowHeight,
}: {
  seriesCount: number;
  rowHeight: number;
}) {
  const template = `${LABEL_COLUMN} repeat(${PLACEHOLDER_BUCKETS}, ${BUCKET_COLUMN}) ${TOTAL_COLUMN}`;
  /*
   * The bars are inset by the loaded table's own gutter rather than spaced with `gap`, which would
   * add 13 gaps the real grid does not have and put every bar under the wrong column.
   */
  const bar = { margin: `0 ${CELL_PAD_X + 6}px`, borderRadius: 3 } as const;

  return (
    <div
      aria-hidden="true"
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius['5xl'],
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: template,
          alignItems: 'center',
          height: HEADER_HEIGHT,
          background: color.surfaceSubtle,
          borderBottom: `1px solid ${color.borderSubtle}`,
        }}
      >
        {Array.from({ length: PLACEHOLDER_BUCKETS + 2 }, (_unused, index) => (
          <span key={index} className="skeleton" style={{ ...bar, height: 8 }} />
        ))}
      </div>

      {Array.from({ length: seriesCount }, (_unused, row) => (
        <div
          key={row}
          style={{
            display: 'grid',
            gridTemplateColumns: template,
            alignItems: 'center',
            height: rowHeight,
            borderTop: row === 0 ? undefined : `1px solid ${color.borderSubtle}`,
          }}
        >
          {Array.from({ length: PLACEHOLDER_BUCKETS + 2 }, (_unused, index) => (
            <span key={index} className="skeleton" style={{ ...bar, height: 12 }} />
          ))}
        </div>
      ))}
    </div>
  );
}
